import { WhereItIs } from "../components/WhereItIs";
import { UserName } from "@/components/UserName";
import { MeasurementsEditor } from "@/components/MeasurementsEditor";
import { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { fromState } from "@/lib/nav";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PhotoGallery } from "@/components/PhotoGallery";
import { ScanTextInput } from "@/components/ScanTextInput";
import { resolveScannedTag, tagStatusWords } from "@/lib/tagScan";
import { PhotoMoveTrail } from "@/components/PhotoFixAction";
import { SourceTruckAction, SourceTruckLine } from "@/components/SourceTruckAction";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { ReturnDialog, ScrapDialog, VoidTagDialog, RetagDialog, UnscrapDialog } from "@/components/ExitActions";
import { AddToCartButton } from "@/components/AddToCartButton";
import { NeedsPutawayBadge } from "@/components/NeedsPutawayBadge";
import { PutAwayDialog } from "@/components/PutAwayDialog";
import { InterchangeCard } from "@/components/InterchangeCard";
import { LockedField } from "@/components/LockedField";
import { PartTagPrintDialog } from "@/components/PartTagPrintDialog";
import { BulkSpawnDialog } from "@/components/BulkSpawnDialog";
import { NotesLog } from "@/components/NotesLog";
import { PhotoViewer } from "@/components/PhotoViewer";
import { MoreActions, MoreSection } from "@/components/MoreActions";
import { SalesMenuButton } from "@/components/sales/SalesMenu";
import { useSales } from "@/lib/sales";
import { KeywordChips } from "@/components/KeywordChips";
import { PartNumbersGrouped } from "@/components/PartNumbersGrouped";
import { ChoiceButton } from "@/components/ChoiceButton";
import { DataplatePanel } from "@/components/DataplatePanel";
import { RefusedUploads } from "@/components/RefusedUploads";
import { SpinOffChildDialog } from "@/components/SpinOffChildDialog";
import { DraftRecoveryBanner, readDrafts, clearDrafts } from "@/components/DraftRecoveryBanner";
import { UnsavedWorkDialog } from "@/components/UnsavedWorkDialog";
import { useDraft, useLeaveWarning } from "@/lib/useDraft";
import { ReferencePicker, buildHandoffPayload, isIdentityNumber } from "@/components/ReferencePicker";
import { BackLink } from "@/components/BackLink";
import { titleCase } from "@/lib/text";
import { toast } from "sonner";
import { ScanField } from "@/components/ScanField";
import { scanAccept, scanRefuse, scanWarn } from "@/lib/scanFeedback";
import { statusLabel, statusColor, partStatusLabel, partStatusColor } from "@/lib/status";
import {
  ArrowLeft, Package, Camera, Pencil, QrCode, Printer, Tag, ShoppingCart, DollarSign, Copy,
  Lock, Loader2, MapPin, Truck, ExternalLink, History as HistoryIcon, Fingerprint, ChevronRight, ChevronDown,
  RotateCcw, Trash2, AlertTriangle, Inbox, Wrench, Scissors, Plus, StickyNote, GitBranch, Sparkles,
  ListChecks, CheckCircle2, Circle, Globe, GraduationCap, ArrowRight, ClipboardList, TicketX, Undo2, Boxes, Cpu } from "lucide-react";

const money = (v) => (v == null ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const TX_LABEL = {
  "part.created": "Captured", "part.finalized": "Set Available", "part.putaway": "Put away",
  "part.split": "Split into children", "tag.reprinted": "Tag reprinted", "tag.bound": "Tag bound",
  "part.status_change": "Status changed", "part.updated": "Edited", "part.photographed": "Photo added",
  "part.released": "Released back to stock", "part.reserved": "Reserved to cart", "cart.reserved": "Reserved to cart",
  "part.removed": "Removed (outtake)", "part.short_pick": "Short-picked", "tag.token_reissued": "Token reissued",
  "part.protected_edit": "Protected field changed", "part.value_set": "Value updated",
};
const txLabel = (t) => TX_LABEL[t] || (t || "").replace(/[._]/g, " ");

function SectionCard({ title, icon: Icon, action, children, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2 font-display font-bold text-slate-800">{Icon && <Icon className="w-4 h-4 text-[#B01E28]" />}{title}</div>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}
function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</div>
      <div className={`text-sm text-slate-800 mt-0.5 ${mono ? "font-mono" : ""}`}>{value || <span className="text-slate-300">—</span>}</div>
    </div>
  );
}

export default function PartDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { has, user } = useAuth();
  const canManage = has("manage_parts");
  const canArchivePhotos = has("archive_photos");
  const canSell = has("flag_sell_candidate");
  const sales = useSales();
  const canReturn = has("process_return");
  const canScrap = has("scrap_writeoff");
  const canRequest = has("submit_request");
  const canPutaway = has("putaway");
  const canReprint = has("reprint_tags");
  const canRelate = has("manage_part_relationships");
  const [data, setData] = useState(null);
  const [corrections, setCorrections] = useState(null);
  const [loading, setLoading] = useState(true);
  const [labelOpen, setLabelOpen] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  const [photosOpen, setPhotosOpen] = useState(false);
  const [refusedTick, setRefusedTick] = useState(0);
  const [donorOpen, setDonorOpen] = useState(false);
  const [splitOpen, setSplitOpen] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  // WORK LEFT UNFINISHED ON THIS PART — read back out of this device's own store, so an
  // interrupted job announces itself instead of waiting to be remembered.
  const [drafts, setDrafts] = useState([]);
  const newSplitChild = () => ({ name: "", tag_code: "", sel: new Set(), keepNum: new Set(), selKw: new Set(), keepKw: new Set(), allowIdentity: false, showRefs: false });
  const [splitChildren, setSplitChildren] = useState([newSplitChild()]);
  const [splitNote, setSplitNote] = useState("");
  const patchChild = (i, patch) => setSplitChildren((s) => s.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  // Camera/QR/gun scan → add a child. Fills the first empty tag row, else appends a new one.
  // Makes split-assembly work on tablets/phones (the tag field used to be type-only).
  const addScannedChild = async (code) => {
    // ONE implementation for every tag box: whatever came off the scanner — a QR URL, the
    // token inside it, or a typed number — becomes the FAS number printed on that tag.
    const r = await resolveScannedTag(code);
    if (!r.code) { scanRefuse(r.error || "COULDN'T READ THAT TAG"); toast.error(r.error || "Couldn't read that tag."); return; }
    if (r.boundPart || r.status === "voided" || r.status === "retired") {
      scanRefuse(tagStatusWords(r)); toast.error(tagStatusWords(r)); return;
    }
    const tag = r.code;
    if (!tag) return;
    if (splitChildren.some((c) => (c.tag_code || "").trim().toUpperCase() === tag)) {
      scanWarn(`${tag} IS ALREADY ON THE LIST`); toast.info(`${tag} is already on the list.`); return;
    }
    scanAccept(tag);
    setSplitChildren((s) => {
      const idx = s.findIndex((c) => !(c.tag_code || "").trim());
      if (idx >= 0) return s.map((x, j) => (j === idx ? { ...x, tag_code: tag } : x));
      return [...s, { ...newSplitChild(), tag_code: tag }];
    });
    toast.success(`Child ${tag} added — name it later in Identify.`);
  };
  const toggleInChildSet = (i, key, val) => setSplitChildren((s) => s.map((x, j) => {
    if (j !== i) return x;
    const n = new Set(x[key]); n.has(val) ? n.delete(val) : n.add(val); return { ...x, [key]: n };
  }));
  const [splitting, setSplitting] = useState(false);
  const [askDiscard, setAskDiscard] = useState(false);
  const [draftBack, setDraftBack] = useState(false);     // we handed work back when it reopened
  // WHAT COUNTS AS WORK: a tag, a name, or references picked to move. An empty first row is not
  // work, and must never raise "discard?" at somebody closing a dialog they opened by mistake.
  const splitStarted = splitChildren.filter((c) => (c.tag_code || "").trim() || (c.name || "").trim()
    || c.sel.size || c.selKw.size || c.keepNum.size || c.keepKw.size);
  const splitWorkCount = splitStarted.length;
  const splitDraft = useDraft({
    key: `split:${id}`,
    enabled: splitOpen,
    snapshot: () => ({
      note: splitNote,
      children: splitChildren.map((c) => ({
        name: c.name || "", tag_code: c.tag_code || "", showRefs: !!c.showRefs,
        sel: [...c.sel], keepNum: [...c.keepNum], selKw: [...c.selKw], keepKw: [...c.keepKw],
      })),
    }),
    restore: (d) => {
      const rows = (d.children || []).map((c) => ({
        ...newSplitChild(), name: c.name || "", tag_code: c.tag_code || "", showRefs: !!c.showRefs,
        sel: new Set(c.sel || []), keepNum: new Set(c.keepNum || []),
        selKw: new Set(c.selKw || []), keepKw: new Set(c.keepKw || []),
      }));
      if (!rows.length) return;
      setSplitChildren(rows); setSplitNote(d.note || ""); setDraftBack(true);
    },
    isWorth: (d) => (d?.children || []).some((c) => (c.tag_code || "").trim() || (c.name || "").trim()
      || (c.sel || []).length || (c.selKw || []).length),
  });
  useLeaveWarning(splitOpen && splitWorkCount > 0);
  // CLOSING WITH WORK IN IT ASKS FIRST — and the work is kept either way, so even "discard" only
  // throws away what he said to throw away.
  const closeSplit = (force = false) => {
    if (!force && splitWorkCount > 0) { setAskDiscard(true); return; }
    setSplitOpen(false); setDraftBack(false);
  };
  const discardSplit = () => {
    splitDraft.clear();
    setSplitChildren([newSplitChild()]); setSplitNote("");
    setAskDiscard(false); setSplitOpen(false); setDraftBack(false);
  };
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkParentCode, setLinkParentCode] = useState("");
  const [linkPreview, setLinkPreview] = useState(null);
  const [linkResolving, setLinkResolving] = useState(false);
  const [linking, setLinking] = useState(false);
  const [spinOpen, setSpinOpen] = useState(false);
  const [spinNumberId, setSpinNumberId] = useState(null);
  // Re-read after any of the three dialogs closes — that is when work is either saved or parked.
  useEffect(() => { setDrafts(readDrafts(id)); }, [id, splitOpen, dupOpen, spinOpen]);
  const openSpinOff = (numberId = null) => { setSpinNumberId(numberId); setSpinOpen(true); };
  const resolveParent = async () => {
    const code = (linkParentCode || "").trim();
    if (!code) return;
    setLinkResolving(true); setLinkPreview(null);
    try {
      const { data: res } = await api.get(`/parts/resolve-scan`, { params: { code } });
      if (res.id === id) { toast.error("A part can't be its own parent."); return; }
      setLinkPreview(res);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't find that part."); }
    finally { setLinkResolving(false); }
  };
  const doLink = async () => {
    if (!linkPreview) return;
    setLinking(true);
    try {
      const { data: res } = await api.post(`/parts/${id}/relationship`, { parent_id: linkPreview.id });
      toast.success(res.op === "reassigned" ? "Parent reassigned — donor tie untouched." : "Linked under parent — donor tie untouched.");
      setLinkOpen(false); setLinkPreview(null); setLinkParentCode(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLinking(false); }
  };
  const doSplit = async () => {
    const children = splitChildren.filter((c) => (c.tag_code || "").trim()).map((c) => ({
      name: (c.name || "").trim(), tag_code: c.tag_code.trim(),
      ...buildHandoffPayload(c.sel, c.keepNum, c.selKw, c.keepKw),
    }));
    if (children.length === 0) { toast.error("Scan or type a tag for at least one child. Naming can wait for Identify."); return; }
    const anyHandoff = children.some((c) => c.move_number_ids.length || c.copy_number_ids.length || c.move_keywords.length || c.copy_keywords.length);
    if (anyHandoff && !splitNote.trim()) { toast.error("Add a note — you're moving references off the parent."); return; }
    setSplitting(true);
    try {
      const { data: res } = await api.post(`/parts/${id}/split`, { children, note: splitNote.trim() });
      const moved = (res.moved_numbers || 0) + (res.moved_keywords || 0);
      toast.success(`Split into ${res.children.length} ${res.children.length === 1 ? "child" : "children"}${moved ? ` — moved ${res.moved_numbers} number(s) & ${res.moved_keywords} keyword(s) off the parent` : " — name them in Sort & Identify"}. Parent stays available.`);
      splitDraft.clear(); setDraftBack(false);
      setSplitOpen(false); setSplitChildren([newSplitChild()]); setSplitNote(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSplitting(false); }
  };
  // Full edits happen in the Identify & Completion workspace (/sort-queue/:id) —
  // one editor, full field parity, donor tie locked. No thin inline editor here.

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/parts/${id}/detail`); setData(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setData(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // IS THERE A DATAPLATE SECTION ON THIS PART AT ALL? An engine gets one (with "No QuickServe
  // dataplate yet" until one is loaded); a brake caliper never does. Cheap read — the panel below
  // owns the plates themselves.
  const [dataplateState, setDataplateState] = useState({ has: false, isEngine: false });
  const [showDataplate, setShowDataplate] = useState(false); // opened from More, before a part is named an engine
  useEffect(() => {
    api.get(`/parts/${id}/dataplates`)
      .then(({ data }) => setDataplateState({ has: (data.plates || []).length > 0, isEngine: !!data.is_engine }))
      .catch(() => setDataplateState({ has: false, isEngine: false }));
  }, [id]);

  // Deep-link: /parts/:id?split=1 opens the Split tool directly. Used by the Floor part
  // card's second-tier "Split" button so a tech lands straight in the hardened split flow.
  const splitDeepLinkDone = useRef(false);
  useEffect(() => {
    if (splitDeepLinkDone.current) return;
    const wants = new URLSearchParams(location.search).get("split") === "1";
    if (wants && canManage && data?.part && data.part.status !== "archived") {
      splitDeepLinkDone.current = true;
      setSplitOpen(true);
    }
    /* eslint-disable-next-line */
  }, [location.search, canManage, data]);

  // Learning diff — what the employee entered vs what the reviewer approved. Fetched
  // separately; shown ONLY to the person who completed it or a reviewer (kept private,
  // never a public scoreboard).
  useEffect(() => {
    let ok = true;
    api.get(`/parts/${id}/corrections`)
      .then(({ data }) => { if (ok) setCorrections(data); })
      .catch(() => { if (ok) setCorrections(null); });
    return () => { ok = false; };
  }, [id]);

  const addPartPhoto = (photo) => {
    if (!photo?.file_id) return;
    setData((prev) => (prev ? { ...prev, part: { ...prev.part, photos: [...(prev.part.photos || []), photo] } } : prev));
  };
  const openLabel = () => setLabelOpen(true);
  const toggleSell = async () => {
    const next = !data.part.sell_candidate;
    try { await api.post(`/parts/${id}/sell-candidate`, { flag: next }); toast.success(next ? "Flagged as sell candidate" : "Sell flag cleared"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (loading) return <div data-testid="part-detail-loading" className="p-16 text-center font-mono text-xs text-slate-400"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />LOADING…</div>;
  if (!data) return <div data-testid="part-detail-missing" className="p-16 text-center text-sm text-slate-500">Part not found. <Link to="/parts" className="text-[#E0781A] underline">Back to parts</Link></div>;

  const p = data.part;
  const donor = data.donor;
  // Carry "you came from this part" onward, so donor/family jumps can bring you back here.
  const originState = fromState(location, p.name || p.tag_code || p.short_code);
  const locElsewhere = p.in_transit ? `In transit to ${p.in_transit.to_site_name}`
    : (p.physical_state === "on_donor" && donor ? `On donor ${donor.stock_number}` : null);
  const heroAccent = (p.scrap || ["archived", "scrap"].includes(p.status)) ? "bg-[#B01E28]"
    : (p.reserved_cart || p.in_transit) ? "bg-amber-500"
      : p.status === "available" ? "bg-emerald-500" : "bg-slate-400";

  // Free-text flag (parity with the Spin-off dialog): if any reference being moved to a
  // split child ALSO appears in the parent's name/notes, warn — search would still surface
  // the parent by that text. We never auto-edit the user's prose.
  const splitFlagged = (() => {
    const hay = `${p.name || ""} ${p.notes || ""}`.toLowerCase();
    const numById = new Map((p.numbers || []).map((n) => [n.id, n.value]));
    const terms = [];
    for (const c of splitChildren) {
      c.sel.forEach((idn) => terms.push(numById.get(idn)));
      c.selKw.forEach((k) => terms.push(k));
    }
    const cleaned = terms.map((t) => String(t || "").toLowerCase()).filter((t) => t.length >= 3);
    return [...new Set(cleaned.filter((t) => hay.includes(t)))];
  })();

  return (
    <div data-testid="part-detail-page" className="max-w-7xl mx-auto">
      <BackLink to="/parts" label="Parts" current={p.name || p.tag_code || p.short_code} testid="part-detail-back" />

      {/* FROZEN HEADER — title + identity + primary action stay visible on scroll. On phones AND tablets the actions
          drop to their own row so the part name gets the whole width (his asks, 27 + 28 Oct). */}
      <div data-testid="part-detail-sticky-header" className="sticky top-0 z-30 -mx-4 px-4 sm:-mx-6 sm:px-6 py-3 mb-4 bg-white/95 backdrop-blur border-b border-slate-200 flex flex-wrap xl:flex-nowrap items-center gap-x-3 gap-y-2">
        <div className={`hidden xl:block w-1.5 self-stretch rounded ${heroAccent}`} />
        <div className="min-w-0 flex-1 basis-full xl:basis-0">
          <div className="flex items-center gap-2 font-mono text-[10px] font-bold text-slate-400 min-w-0">
            <Package className="w-3.5 h-3.5 text-[#B01E28] shrink-0" />
            <span className="truncate">{p.tag_code || p.short_code}</span>
            <Badge data-testid="part-sticky-status" className={`shrink-0 rounded text-white text-[9px] uppercase font-mono ${partStatusColor(p)}`}>{partStatusLabel(p)}</Badge>
            {p.grade && <span className="hidden sm:inline shrink-0 whitespace-nowrap text-slate-400">· Grade {p.grade}</span>}
          </div>
          {/* wraps to two lines on every screen (28 Oct: tablets too); the full name is on hover/long-press */}
          <div data-testid="part-sticky-title" title={p.name || undefined} className="font-display text-base sm:text-lg font-black tracking-tight text-slate-900 leading-snug line-clamp-2 break-words">{p.name || "Unnamed part — needs ID"}</div>
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-auto">
          <div className="[&_button]:h-11 [&_button]:px-5 [&_button]:text-base [&_button]:rounded-xl [&_button]:shadow-sm">
            <AddToCartButton part={p} onAdded={load} />
          </div>
          <MoreActions testid="part-detail-more">
            {/* Everyday (production-facing) actions */}
            {canRequest && <Button data-testid="part-detail-request" variant="outline" onClick={() => navigate(`/requests?new=verify&part=${encodeURIComponent(p.tag_code || p.short_code)}`)} className="rounded-lg border-slate-300 h-9"><Inbox className="w-4 h-4 mr-1.5" />Request / Verify</Button>}
            {canSell && !sales.any && <Button data-testid="part-detail-sell-toggle" variant="outline" onClick={toggleSell} className={`rounded-lg h-9 ${p.sell_candidate ? "border-amber-300 text-amber-700" : "border-slate-300"}`}><Tag className="w-4 h-4 mr-1.5" />{p.sell_candidate ? "Clear sell flag" : "Flag to sell"}</Button>}
            {/* STAGE 2 · S2.1: with Sales switched on, the Sales menu sits where "Flag to sell" was (for a Sales
                permission holder; anyone holding only the old flag keeps their button) */}
            <SalesMenuButton kind="part" id={id} label={p.tag_code || p.short_code || "this part"} />
            {/* Warehouse / management actions — only shown when the role holds them */}
            {(canManage || canRelate || canPutaway || canReturn || canScrap) && <MoreSection label="Warehouse actions" />}
            {canManage && <Button data-testid="part-detail-edit" onClick={() => navigate(`/sort-queue/${id}`, { state: originState })} variant="outline" className="rounded-lg border-slate-300 h-9"><Pencil className="w-4 h-4 mr-1.5" />Edit details</Button>}
            {canManage && p.status !== "archived" && <Button data-testid="part-detail-split" variant="outline" onClick={() => setSplitOpen(true)} className="rounded-lg border-slate-300 h-9"><Scissors className="w-4 h-4 mr-1.5" />Split</Button>}
            {canManage && has("generate_print_labels") && p.status !== "archived" && <Button data-testid="part-detail-duplicate" variant="outline" onClick={() => setDupOpen(true)} className="rounded-lg border-slate-300 h-9"><Copy className="w-4 h-4 mr-1.5" />Duplicate ×N</Button>}
            {canRelate && !p.reserved_cart_id && !p.in_transit && !["archived", "scrap"].includes(p.status) && <Button data-testid="part-detail-link-parent" variant="outline" onClick={() => { setLinkParentCode(""); setLinkPreview(null); setLinkOpen(true); }} className="rounded-lg border-slate-300 h-9"><GitBranch className="w-4 h-4 mr-1.5" />{data.family?.parent ? "Change parent" : "Link parent"}</Button>}
            {canPutaway && !p.in_transit && !p.scrap && !["archived", "scrap", "defective", "core"].includes(p.status) && <PutAwayDialog part={p} onDone={load} trigger={<Button data-testid="part-detail-putaway" variant="outline" className="rounded-lg border-emerald-300 text-emerald-700 hover:bg-emerald-50 h-9"><MapPin className="w-4 h-4 mr-1.5" />Put away / Move here</Button>} />}
            {canReturn && !["available", "archived", "scrap", "defective", "core", "on_donor", "in_transit", "staging_needs_id", "staging_needs_grade", "staging_needs_price"].includes(p.status) && <ReturnDialog part={p} onDone={load} trigger={<Button data-testid="part-detail-return" variant="outline" className="rounded-lg border-slate-300 h-9"><RotateCcw className="w-4 h-4 mr-1.5" />Return to stock</Button>} />}
            {canScrap && !p.scrap && !["archived", "scrap", "defective", "core", "in_transit"].includes(p.status) && <ScrapDialog part={p} onDone={load} trigger={<Button data-testid="part-detail-scrap" variant="outline" className="rounded-lg border-[#B01E28] text-[#B01E28] hover:bg-red-50 h-9"><Trash2 className="w-4 h-4 mr-1.5" />Scrap / write-off</Button>} />}
            {canScrap && !p.needs_retag && !p.archived && <VoidTagDialog part={p} onDone={load} trigger={<Button data-testid="part-detail-void-tag" variant="outline" className="rounded-lg border-[#B01E28] text-[#B01E28] hover:bg-red-50 h-9"><TicketX className="w-4 h-4 mr-1.5" />Void tag (mis-assigned)</Button>} />}
            {/* SAME CLASS OF CORRECTION as void-tag and reverse-scrap, so it sits with them (his
                instinct, and it's right — none of these are everyday production actions). */}
            <SourceTruckAction part={p} onDone={load} testid="part-more-source" />
            {canScrap && p.needs_retag && <RetagDialog part={p} onDone={load} trigger={<Button data-testid="part-detail-retag" variant="outline" className="rounded-lg border-[#E0781A] text-[#E0781A] hover:bg-orange-50 h-9"><Tag className="w-4 h-4 mr-1.5" />Re-tag part</Button>} />}
            {canScrap && p.scrap && p.archived && <UnscrapDialog part={p} onDone={load} trigger={<Button data-testid="part-detail-unscrap" variant="outline" className="rounded-lg border-emerald-600 text-emerald-700 hover:bg-emerald-50 h-9"><Undo2 className="w-4 h-4 mr-1.5" />Reverse scrap</Button>} />}
            {canManage && <Button data-testid="part-detail-tag" variant="outline" onClick={openLabel} className="rounded-lg border-slate-300 h-9"><QrCode className="w-4 h-4 mr-1.5" />View tag / QR</Button>}
            {/* HIS QUESTION, 2026-10-17: *"Can I add it from the engine assembly I'm working on, or
                only from the truck?"* From the part — and from here even before the part is NAMED an
                engine, so it is never out of reach while he is identifying it. */}
            {canManage && !dataplateState.has && (
              <Button data-testid="part-detail-add-dataplate" variant="outline"
                onClick={() => { setShowDataplate(true); setTimeout(() => document.querySelector('[data-testid="part-detail-dataplate"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 120); }}
                className="rounded-lg border-slate-300 h-9"><Cpu className="w-4 h-4 mr-1.5" />Add engine dataplate</Button>
            )}
          </MoreActions>
        </div>
      </div>

      <DraftRecoveryBanner partId={id} drafts={drafts}
        onResume={(kind) => { if (kind === "split") setSplitOpen(true); else if (kind === "bulkspawn") setDupOpen(true); else setSpinOpen(true); }}
        onDiscard={(pid) => { clearDrafts(pid); setDrafts([]); toast.success("Unfinished work discarded"); }} />

      {canManage && has("generate_print_labels") && <BulkSpawnDialog part={p} donor={data.donor} lot={data.lot} open={dupOpen} onOpenChange={setDupOpen} onDone={load} />}

      {p.post_signoff_correction && (
        <div data-testid="part-postsignoff-note" className="rounded-xl border border-amber-300 bg-amber-50 p-3 flex items-start gap-2">
          <Undo2 className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
          <div className="text-sm">
            <div className="font-semibold text-amber-800">Corrected after sign-off — returned to stock from closed order {p.post_signoff_correction.cart_number}{p.post_signoff_correction.reason_label ? ` (${p.post_signoff_correction.reason_label})` : ""}</div>
            <div className="text-[11px] text-slate-500 mt-0.5">by {p.post_signoff_correction.by || "—"}{p.post_signoff_correction.at ? ` · ${new Date(p.post_signoff_correction.at).toLocaleString()}` : ""}. The original order stays as it was signed off.</div>
          </div>
        </div>
      )}

      {p.scrap && (p.scrap.note || p.scrap.label) && (
        <div data-testid="part-scrap-note" className="rounded-xl border border-[#B01E28]/30 bg-red-50 p-3 flex items-start gap-2">
          <Trash2 className="w-4 h-4 text-[#B01E28] mt-0.5 shrink-0" />
          <div className="text-sm">
            <div className="font-semibold text-[#B01E28]">{p.scrap.label || "Scrapped"}{p.scrap.note ? ` — ${p.scrap.note}` : ""}</div>
            {(p.scrap.by || p.scrap.at) && <div className="text-[11px] text-slate-500 mt-0.5">by {p.scrap.by || "—"}{p.scrap.at ? ` · ${new Date(p.scrap.at).toLocaleString()}` : ""}</div>}
          </div>
        </div>
      )}

      {/* SPLIT — big photo pinned LEFT (capped width) | details scroll RIGHT; stacks on phone */}
      <div className="lg:flex lg:gap-6 lg:items-start">
        <div className="lg:w-1/2 lg:max-w-[600px] lg:sticky lg:top-[92px] self-start shrink-0 lg:max-h-[calc(100vh-92px)] lg:overflow-y-auto" data-testid="part-detail-hero-photos">
          {/* the photo buttons sit ON the page under the photos, never inside the More menu: a menu that closes while
              the phone's camera is open would take the picked photo with it */}
          <PhotoViewer photos={p.photos} testidPrefix="part-detail" heightClass="h-[46dvh] lg:h-[calc(100vh-18rem)]"
            uploadEndpoint={canManage ? `/parts/${id}/photos` : undefined} onUploaded={addPartPhoto}
            onUploadFailed={() => setRefusedTick((t) => t + 1)}
            fixScope="part" fixRecordId={id} onFixed={load}
            main={{ scope: "part", recordId: id, chosen: p.main_photo_file_id, resolved: p.main_photo?.file_id, onChanged: load }} />
          <div className="mt-2"><RefusedUploads params={{ part_id: id, where: "part_photo" }} refreshKey={refusedTick} testid="part-photo-refused" /></div>
          <div className="mt-2"><PhotoMoveTrail scope="part" recordId={id} onDone={load} testid="part-photo-moves" /></div>
          {p.legacy && <Badge data-testid="part-detail-legacy-badge" className="mt-2 rounded text-[9px] bg-[#B01E28] text-white uppercase font-mono">Legacy / Pre-system</Badge>}
        </div>

        <div className="flex-1 min-w-0 space-y-5 mt-6 lg:mt-0">
          {/* LEARNING DIFF — private teaching moment: what you entered vs what was approved,
              plus the reviewer's optional note. Shown ONLY to the person who completed the
              part or a reviewer — never a public scoreboard. */}
          {corrections?.has_corrections
            && (has("review_parts") || corrections.completed_by === user?.email || p.completed_by === user?.email) && (
            <div data-testid="part-corrections-panel" className="rounded-xl border border-emerald-200 bg-emerald-50/60 shadow-sm overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-emerald-200 bg-emerald-50">
                <GraduationCap className="w-4 h-4 text-emerald-700" />
                <span className="font-display font-bold text-emerald-900 text-sm">
                  {corrections.action === "returned" ? "Returned for rework — feedback" : "What changed in review"}
                </span>
                {corrections.reviewed_by && <span className="ml-auto text-[11px] text-emerald-600 font-mono truncate">reviewed by {corrections.reviewed_by.split("@")[0]}</span>}
              </div>
              <div className="p-5 space-y-3">
                <p className="text-xs text-emerald-700">A private note to help you next time — compare what you entered against the approved version.</p>
                {corrections.note && (
                  <div data-testid="part-corrections-note" className="rounded-lg bg-white border border-emerald-200 px-3 py-2 text-sm text-emerald-900 whitespace-pre-wrap">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-500 block mb-0.5">Teaching note</span>
                    {corrections.note}
                  </div>
                )}
                {(corrections.changes || []).length > 0 && (
                  <div data-testid="part-corrections-diff" className="space-y-2">
                    {corrections.changes.map((c, i) => (
                      <div key={i} data-testid={`part-correction-${c.field}`} className="rounded-lg bg-white border border-slate-200 px-3 py-2">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">{c.label || c.field}</div>
                        <div className="flex flex-wrap items-center gap-2 text-sm">
                          <span className="rounded bg-red-50 text-red-700 line-through px-2 py-0.5 break-all">{c.from || <span className="italic no-underline text-slate-400">empty</span>}</span>
                          <ArrowRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <span className="rounded bg-emerald-100 text-emerald-800 font-semibold px-2 py-0.5 break-all">{c.to || <span className="italic text-slate-400">empty</span>}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className={`h-1 ${heroAccent}`} />
            <div className="p-5">
              <h1 data-testid="part-detail-title" className="font-display text-2xl font-black tracking-tight text-slate-900">{p.name || "Unnamed part — needs ID"}</h1>
              {(donor || p.source) && (
                <div data-testid="part-detail-truck" className="mt-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 flex items-start gap-2">
                  <Truck className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">From truck</div>
                    {donor ? (
                      <Link to={`/donors/${donor.id}`} state={originState} className="font-semibold hover:text-[#E0781A]">
                        {[donor.year, titleCase(donor.make || ""), titleCase(donor.model || "")].filter(Boolean).join(" ") || "Donor truck"} · <span className="font-mono">{donor.stock_number}</span>
                      </Link>
                    ) : <span className="capitalize font-semibold">{p.source} source</span>}
                  </div>
                </div>
              )}
              {/* THE LOT IT CAME FROM, AND IT STILL OPENS AFTER THE LOT IS CLOSED — his rule,
                  2026-10-07: closing a lot is "done moving", not "gone". A legacy lot is the
                  record of where a group of parts came from, so this is the way back to it. */}
              {data.lot && (
                <div data-testid="part-detail-lot" className="mt-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 flex items-start gap-2">
                  <Boxes className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                      From {data.lot.kind === "legacy" ? "legacy lot" : "lot"}
                    </div>
                    <Link to={`/capture?lot=${data.lot.id}`} state={originState}
                      data-testid="part-detail-lot-link" className="font-semibold hover:text-[#E0781A]">
                      <span className="font-mono">{data.lot.batch_number}</span>
                      {data.lot.name && data.lot.name !== data.lot.batch_number ? ` · ${data.lot.name}` : ""}
                    </Link>
                  </div>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <Badge data-testid="part-detail-status" className={`rounded-md text-white text-[10px] uppercase font-mono ${partStatusColor(p)}`}>{partStatusLabel(p)}</Badge>
                {p.pending_completion && <Badge data-testid="part-detail-needs-id" className="rounded-md bg-amber-500 text-white border border-amber-600 text-[10px] uppercase font-mono"><ClipboardList className="w-3 h-3 mr-1" />Needs ID{p.pending_completion_ctx ? ` · ${p.pending_completion_ctx}` : ""}</Badge>}
                <NeedsPutawayBadge part={p} className="text-[10px]" />
                {p.reserved_cart && <Badge data-testid="part-detail-incart" className="rounded-md bg-amber-100 text-amber-800 border border-amber-200 text-[10px] uppercase font-mono"><ShoppingCart className="w-3 h-3 mr-1" />In Cart{p.reserved_cart.po_number ? ` · PO ${p.reserved_cart.po_number}` : ""}</Badge>}
                {p.grade && <Badge variant="outline" className="rounded-md text-[10px] font-mono">Grade {p.grade}</Badge>}
                {p.category_name && <span className="text-xs text-slate-500">{titleCase(p.category_name)}{p.part_type_name ? ` · ${titleCase(p.part_type_name)}` : ""}</span>}
                {(p.condition_flags || []).map((f) => <Badge key={f} className="rounded-md bg-slate-100 text-slate-600 text-[9px] font-mono uppercase">{f}</Badge>)}
                {p.sell_candidate && <Badge data-testid="part-detail-sell-badge" className="rounded-md bg-amber-100 text-amber-800 border border-amber-200 text-[10px] uppercase font-mono"><Tag className="w-3 h-3 mr-1" />Sell candidate</Badge>}
                {p.ai_unverified && <Badge data-testid="part-detail-ai-unverified" className="rounded-md bg-red-50 text-[#B01E28] border border-dashed border-[#B01E28] text-[10px] uppercase font-mono"><Sparkles className="w-3 h-3 mr-1" />AI-assisted · verify</Badge>}
                {p.unknown_unverified && <Badge data-testid="part-detail-unknown-unverified" className="rounded-md bg-slate-100 text-slate-600 border border-slate-300 text-[10px] uppercase font-mono">Unknown / unverified</Badge>}
              </div>
              {/* WHAT THE TRUCK SAID ABOUT THIS COMPONENT — and any bench disagreement with it.
                  Both readings stay visible: losing the first one means selling a blown engine as
                  good, and hiding the second one means pretending nobody looked again. */}
              {p.condition_inherited?.state && (
                <div data-testid="part-detail-inherited-condition"
                  className={`mt-2 rounded-lg border px-2.5 py-2 text-[11px] ${p.condition_inherited.state === "present_bad" ? "border-[#B01E28] bg-red-50 text-[#B01E28]" : "border-slate-200 bg-slate-50 text-slate-600"}`}>
                  <b className="uppercase tracking-wider">
                    On {p.condition_inherited.donor_stock || "its truck"} this was rated {p.condition_inherited.state === "present_bad" ? "KNOWN BAD" : p.condition_inherited.state === "present_good" ? "GOOD" : p.condition_inherited.state === "gone" ? "GONE" : "PRESENT, UNTESTED"}
                    {p.condition_inherited.reason_label ? ` — ${p.condition_inherited.reason_label}` : ""}
                  </b>
                  <span className="text-slate-500"> · {p.condition_inherited.by || "someone"}{p.condition_inherited.at ? ` · ${String(p.condition_inherited.at).slice(0, 10)}` : ""}</span>
                  {p.condition_override?.reason && (
                    <div data-testid="part-detail-condition-override" className="text-amber-700 mt-1">
                      Overruled at the bench by {p.condition_override.by}: “{p.condition_override.reason}”
                    </div>
                  )}
                </div>
              )}
              {p.ai_unverified && (p.ai_estimated_fields || []).length > 0 && (
                <div data-testid="part-detail-ai-fields" className="mt-2 flex items-start gap-1.5 text-[11px] text-[#B01E28]">
                  <Sparkles className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>AI-assisted, not yet human-verified: <span className="font-semibold">{(p.ai_estimated_fields || []).map((f) => f.replace(/_/g, " ")).join(", ")}</span> — verify before relying on these.</span>
                </div>
              )}

              {dataplateState.isEngine && !dataplateState.has && (
                <button type="button" data-testid="part-detail-dataplate-nudge"
                  onClick={() => document.querySelector('[data-testid="part-detail-dataplate"]')?.scrollIntoView({ behavior: "smooth", block: "start" })}
                  className="mt-3 w-full flex items-center gap-2 rounded-lg border-2 border-amber-300 bg-amber-50 px-3 py-2 text-left text-sm font-bold text-amber-900 hover:bg-amber-100 transition-colors">
                  <Cpu className="w-4 h-4 shrink-0" />
                  No engine dataplate on this part yet — add the Cummins dataplate PDF
                </button>
              )}

              {/* WHERE IT IS, FOUND WITHOUT LOOKING FOR IT — his instruction, 2026-10-17. The
                  heaviest block on the page after the name, amber like the floor view, and the
                  whole walk in one readable line that never gets cut. */}
              <div data-testid="part-detail-hero-location"
                className={`mt-4 rounded-xl border-2 px-4 py-3 flex items-start gap-3 ${p.location_missing
                  ? "border-[#B01E28] bg-red-50" : p.location ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-slate-50"}`}>
                <MapPin className={`w-7 h-7 mt-0.5 shrink-0 ${p.location_missing ? "text-[#B01E28]" : p.location ? "text-emerald-600" : "text-slate-400"}`} />
                <div className="min-w-0">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Where it is</div>
                  {p.location
                    ? <WhereItIs location={p.location} size="xlPc" icon={false} testid="part-detail-where" />
                    : <div data-testid="part-detail-where" className={`text-lg font-bold ${p.location_missing ? "text-[#B01E28]" : "text-slate-800"}`}>
                        {locElsewhere || (p.location_missing
                          ? "The place it was scanned into no longer exists — scan it onto a shelf"
                          : "No location recorded")}</div>}
                </div>
              </div>

              {/* fitment & physical live in the bottom 'Fitment & physical' card (low priority) */}

              {p.reserved_cart && (
                <div data-testid="part-detail-reserved-callout" className="mt-4 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  <ShoppingCart className="w-4 h-4 mt-0.5 shrink-0 text-amber-700" />
                  <div>
                    <span className="font-semibold">Reserved for {p.reserved_cart.cart_number}</span>
                    {p.reserved_cart.po_number ? <span className="font-mono"> · PO {p.reserved_cart.po_number}</span> : null}
                    {p.reserved_cart.assignment_type ? <span className="text-amber-700"> · {p.reserved_cart.assignment_type.replace("_", " ")}</span> : null}
                    <span className="block text-xs text-amber-700 mt-0.5">This part is promised to a job — tell that job before pulling it for something else.</span>
                  </div>
                </div>
              )}
              {(() => {
                const reprints = (data.history || []).filter((h) => h.type === "tag.reprinted");
                if (!reprints.length) return null;
                const last = reprints[reprints.length - 1];
                const when = last.timestamp ? new Date(last.timestamp).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "";
                return (
                  <div data-testid="reprint-history" className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-500">
                    <Tag className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                    <span><b className="text-slate-700">Reprinted {reprints.length}×</b>{last.reason ? ` · “${last.reason}”` : ""}{when ? ` · ${when}` : ""}{last.user_email ? ` · ${last.user_email}` : ""}</span>
                  </div>
                );
              })()}
              <div data-testid="part-detail-audit" className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap gap-x-4 gap-y-1 text-[10px] font-mono text-slate-400">
                {p.created_by && <span>Captured by <span className="text-slate-500">{p.created_by}</span>{p.created_at ? ` · ${(p.created_at || "").slice(0, 10)}` : ""}</span>}
                {p.updated_by && <span>Last edited by <span className="text-slate-500">{p.updated_by}</span>{p.updated_at ? ` · ${(p.updated_at || "").slice(0, 10)}` : ""}</span>}
              </div>
            </div>
          </div>

      {/* NEEDS RE-TAG banner (tag was voided, awaiting a fresh tag) */}
      {p.needs_retag && (
        <div data-testid="part-detail-needs-retag-banner" className="border border-amber-300 bg-amber-50 rounded-xl p-4 flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm flex-1">
            <span className="font-bold text-amber-800">Needs a fresh tag.</span>{" "}
            <span className="text-slate-600">Its previous tag was voided — this part can't be scanned until a new tag is married to it. All its data is intact.</span>
          </div>
          {canScrap && <RetagDialog part={p} onDone={load} trigger={<Button data-testid="part-detail-retag-banner" size="sm" className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white shrink-0"><Tag className="w-4 h-4 mr-1.5" />Re-tag</Button>} />}
        </div>
      )}

      {/* SCRAP / WRITE-OFF banner */}
      {p.scrap && (
        <div data-testid="part-detail-scrap-banner" className="border border-red-200 bg-red-50 rounded-xl p-4 flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-[#B01E28] shrink-0 mt-0.5" />
          <div className="text-sm">
            <span className="font-bold text-[#B01E28]">Written off — {p.scrap.label}.</span>{" "}
            <span className="text-slate-600">{p.scrap.note}</span>
            <div className="font-mono text-[10px] text-slate-400 mt-0.5">by <UserName email={p.scrap.by} /> · archived (not deleted) · credits nothing to recovery</div>
          </div>
        </div>
      )}

      {/* IN-TRANSIT banner */}
      {p.in_transit && (
        <div data-testid="part-detail-transit-banner" className="border border-indigo-200 bg-indigo-50 rounded-xl p-4 flex items-start gap-2">
          <Truck className="w-5 h-5 text-indigo-600 shrink-0 mt-0.5" />
          <div className="text-sm">
            <span className="font-bold text-indigo-700">In transit to {p.in_transit.to_site_name}.</span>{" "}
            <span className="text-slate-600">Transfer {p.in_transit.transfer_number}. It can't be carted, picked, or put away until it's received.</span>
          </div>
        </div>
      )}

      {/* IDENTIFICATION */}
      <SectionCard testid="part-detail-identification" title="Identification" icon={Fingerprint}>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <Field label="Manufacturer / brand" value={p.brand ? titleCase(p.brand) : (p.legacy ? "Unknown / not recorded" : "")} />
          <Field label="OEM number" value={p.oem_number} mono />
          <Field label="Serial / tag number" value={p.tag_serial} mono />
          <Field label="Superseded number(s)" value={(p.superseded_numbers || []).join(", ")} mono />
        </div>
        {p.description && (
          <div className="mt-4" data-testid="part-detail-description">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Description</div>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{p.description}</p>
          </div>
        )}
        {(p.numbers || []).length > 0 && (
          <div className="mt-4" data-testid="part-detail-numbers">
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Part numbers</div>
              <div className="flex items-center gap-2">
                {p.numbers_need_reconcile && <Badge data-testid="part-detail-numbers-reconcile" className="rounded-full border border-amber-300 bg-amber-50 text-amber-700 text-[10px]"><AlertTriangle className="w-3 h-3 mr-0.5" />reconcile — sources disagree</Badge>}
                {canManage && <ChoiceButton tone="danger" data-testid="spinoff-open" onClick={() => openSpinOff(null)} className="h-8 min-h-8"><GitBranch className="w-3.5 h-3.5 mr-1" />Spin off a component</ChoiceButton>}
              </div>
            </div>
            <div className="space-y-1.5">
              <PartNumbersGrouped part={p} canManage={canManage} onChanged={load} onSpinOff={openSpinOff} />
            </div>
          </div>
        )}
        {(p.keywords || []).length > 0 && (
          <div className="mt-4" data-testid="part-detail-keywords">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Keywords / tags</div>
            <div className="flex flex-wrap gap-1.5">
              {p.keywords.map((k, i) => (
                <span key={i} data-testid={`part-keyword-${i}`} className="inline-flex items-center gap-1 rounded-full bg-[#FBEFE2] text-[#B45309] text-xs font-semibold px-2.5 py-1">
                  <Tag className="w-3 h-3" />{k}
                </span>
              ))}
            </div>
          </div>
        )}
        {(p.relationships || []).filter((r) => r.type === "spin_off").length > 0 && (
          <div className="mt-4" data-testid="part-detail-spinoffs">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5 flex items-center gap-1.5"><GitBranch className="w-3.5 h-3.5" />Spun-off components</div>
            <div className="space-y-1.5">
              {(p.relationships || []).filter((r) => r.type === "spin_off").map((r, i) => (
                <div key={i} data-testid={`part-detail-spinoff-${i}`} className="rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-600">{r.note}</span>
                    <button data-testid={`spinoff-child-link-${i}`} onClick={() => navigate(`/parts/${r.child_id}`, { state: originState })} className="ml-auto font-mono font-bold text-[#B01E28] hover:underline shrink-0">{r.child_tag} →</button>
                  </div>
                  <div className="text-[11px] text-slate-400 mt-1">
                    Moved: {[...(r.moved_numbers || []), ...(r.moved_keywords || [])].join(", ") || "—"} · {r.by} · {r.at ? new Date(r.at).toLocaleDateString() : ""}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {(p.measurements || []).length > 0 && (
          <div className="mt-4" data-testid="part-detail-measurements">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Measurements</div>
            <MeasurementsEditor partId={p.id} measurements={p.measurements} readOnly />
          </div>
        )}
        {(p.interchange_numbers || []).length > 0 && (
          <div className="mt-4">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Interchange / alternate numbers</div>
            <div className="flex flex-wrap gap-2">
              {p.interchange_numbers.map((ic, i) => (
                <span key={i} data-testid={`part-interchange-${i}`} className="inline-flex items-center gap-1.5 border border-slate-200 rounded-lg px-2.5 py-1 bg-slate-50">
                  <span className="font-mono text-xs font-medium text-slate-800">{ic.number}</span>
                  {(ic.type || ic.source) && <span className="font-mono text-[9px] uppercase text-slate-400">{[ic.type, ic.source].filter(Boolean).join(" · ")}</span>}
                </span>
              ))}
            </div>
          </div>
        )}
      </SectionCard>

      {/* TEARDOWN PROGRESS — read-only view of the parent→child hand-off trail: what's
          still on the assembly vs what's already been spun off. Rides on existing data
          (numbers[]/keywords[] = still-on; spin_off relationships + family = spun-off). */}
      {(() => {
        const spinoffs = (p.relationships || []).filter((r) => r.type === "spin_off");
        if (spinoffs.length === 0) return null;
        const childById = new Map((data.family?.children || []).map((c) => [c.id, c]));
        const componentNums = (p.numbers || []).filter((n) => !isIdentityNumber(n, p.oem_normalized, p.oem_number));
        const identityNums = (p.numbers || []).filter((n) => isIdentityNumber(n, p.oem_normalized, p.oem_number));
        const stillOnKw = p.keywords || [];
        const stillCount = componentNums.length + stillOnKw.length;
        return (
          <SectionCard testid="part-detail-teardown" title="Teardown progress" icon={ListChecks}
            action={
              <span data-testid="teardown-progress-chip" className="inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-2.5 py-1 text-slate-700 bg-slate-100 border border-slate-200">
                {stillCount} still on · {spinoffs.length} spun off
              </span>
            }>
            {/* SPUN OFF */}
            <div data-testid="teardown-spun-off">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5 flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />Spun off ({spinoffs.length})</div>
              <div className="space-y-1.5">
                {spinoffs.map((r, i) => {
                  const child = childById.get(r.child_id);
                  const moved = [...(r.moved_numbers || []), ...(r.moved_keywords || [])];
                  return (
                    <div key={i} data-testid={`teardown-spinoff-${i}`} className="rounded-lg border border-slate-200 bg-emerald-50/40 px-3 py-2 text-xs">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                        <span className="font-medium text-slate-700 truncate">{moved.length ? moved.join(", ") : (r.note || "Component spun off")}</span>
                        {child ? (
                          <button data-testid={`teardown-child-link-${i}`} onClick={() => navigate(`/parts/${r.child_id}`, { state: originState })}
                            className="ml-auto flex items-center gap-1.5 shrink-0">
                            <span className="font-mono font-bold text-[#B01E28] hover:underline">{r.child_tag}</span>
                            <Badge className={`rounded-md text-[9px] font-mono uppercase ${statusColor(child.status)}`}>{statusLabel(child.status)}</Badge>
                          </button>
                        ) : (
                          <span data-testid={`teardown-child-missing-${i}`} className="ml-auto inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 shrink-0">
                            <AlertTriangle className="w-3 h-3" />{r.child_tag || "child"} — archived or unlinked
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-400 mt-1">{r.by}{r.at ? ` · ${new Date(r.at).toLocaleDateString()}` : ""}</div>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* STILL ON */}
            <div className="mt-4" data-testid="teardown-still-on">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5 flex items-center gap-1.5"><Circle className="w-3.5 h-3.5 text-slate-400" />Still on the assembly ({stillCount})</div>
              {stillCount === 0 ? (
                <p data-testid="teardown-none-left" className="text-xs text-slate-500 italic">Every catalogued component number/keyword has been spun off. Only the assembly's own identity remains.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {componentNums.map((n, i) => (
                    <span key={n.id || i} data-testid={`teardown-still-num-${i}`} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs">
                      <Circle className="w-2.5 h-2.5 text-slate-300" /><span className="font-mono font-bold text-slate-800">{n.value}</span>
                    </span>
                  ))}
                  {stillOnKw.map((k, i) => (
                    <span key={`kw-${i}`} data-testid={`teardown-still-kw-${i}`} className="inline-flex items-center gap-1 rounded-full bg-[#FBEFE2] text-[#B45309] text-xs font-semibold px-2.5 py-1">
                      <Tag className="w-3 h-3" />{k}
                    </span>
                  ))}
                </div>
              )}
              {identityNums.length > 0 && (
                <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="teardown-identity">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-amber-600">Assembly identity (stays)</span>
                  {identityNums.map((n, i) => (
                    <span key={n.id || i} className="font-mono font-bold text-xs text-slate-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">{n.value}</span>
                  ))}
                </div>
              )}
            </div>
          </SectionCard>
        );
      })()}

      {/* INTERCHANGE & SUPERSESSION */}
      <InterchangeCard partId={p.id} />

      {/* ASSEMBLY / FAMILY — parent <-> child navigation (read-only) */}
      {(() => {
        const fam = data.family || {};
        if (!fam.parent && !(fam.children && fam.children.length)) return null;
        return (
          <SectionCard testid="part-detail-family" title="Assembly / Family" icon={GitBranch}
            action={fam.child_count > 0 ? (
              <span data-testid="family-needs-identify-chip" className={`inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-2.5 py-1 ${fam.needs_identify_count > 0 ? "text-amber-800 bg-amber-50 border border-amber-200" : "text-emerald-800 bg-emerald-50 border border-emerald-200"}`}>
                {fam.child_count} {fam.child_count === 1 ? "child" : "children"}
                {fam.needs_identify_count > 0 ? ` · ${fam.needs_identify_count} need identify` : " · all identified"}
              </span>
            ) : null}>
            {fam.parent && (
              <div className="mb-4">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Part of assembly (parent)</Label>
                <button data-testid="family-parent-link" onClick={() => navigate(`/parts/${fam.parent.id}`, { state: originState })}
                  className="mt-1.5 w-full flex items-center gap-3 rounded-lg border border-slate-200 hover:border-[#E0781A] hover:bg-orange-50/40 px-3 py-2.5 transition-colors text-left">
                  <ChevronRight className="w-4 h-4 text-[#E0781A] rotate-180" />
                  <span className="font-mono text-xs font-bold text-[#B01E28]">{fam.parent.tag_code || fam.parent.short_code}</span>
                  <span className="flex-1 text-sm font-medium text-slate-800 truncate">{fam.parent.name || <span className="italic text-slate-400">Unnamed</span>}</span>
                  <Badge className={`rounded-md text-[9px] font-mono uppercase ${statusColor(fam.parent.status)}`}>{statusLabel(fam.parent.status)}</Badge>
                </button>
              </div>
            )}
            {fam.children && fam.children.length > 0 && (
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Children (split from this part)</Label>
                <div className="mt-1.5 divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
                  {fam.children.map((c) => (
                    <button key={c.id} data-testid={`family-child-${c.id}`} onClick={() => navigate(`/parts/${c.id}`, { state: originState })}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-orange-50/40 transition-colors text-left">
                      <Package className="w-3.5 h-3.5 text-[#B01E28] shrink-0" />
                      <span className="font-mono text-xs font-bold text-[#B01E28] w-28 shrink-0">{c.tag_code || c.short_code}</span>
                      <span className="flex-1 text-sm font-medium text-slate-800 truncate">
                        {c.name || <span className="italic text-amber-700">Unnamed — needs identify</span>}
                      </span>
                      <Badge className={`rounded-md text-[9px] font-mono uppercase ${statusColor(c.status)}`}>{statusLabel(c.status)}</Badge>
                      <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </SectionCard>
        );
      })()}

      {/* NOTES — moved up so they're visible, not buried */}
      {/* ENGINE DATAPLATE — shown on an engine part, and on any part that already has one. The
          plate followed the engine off the truck; nothing was re-typed and nothing was copied. */}
      {(dataplateState.has || dataplateState.isEngine || showDataplate) && (
        <SectionCard testid="part-detail-dataplate" title="Engine dataplate" icon={Cpu}>
          <DataplatePanel kind="part" id={id} canManage={canManage} canVerify={canManage} testid="part-dataplate" />
        </SectionCard>
      )}

      <SectionCard testid="part-detail-notes" title="Notes" icon={StickyNote}>
        <NotesLog recordType="part" recordId={id} canAdd={canManage} />
      </SectionCard>

      {/* PRICING */}
      {(data.can_view_pricing || data.can_view_cost) && (
        <SectionCard testid="part-detail-pricing" title="Pricing" icon={DollarSign}>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {data.can_view_pricing && <Field label="Retail / list price" value={money(p.retail_price)} mono />}
            {data.can_view_pricing && <Field label="Customer price (public)" value={money(p.public_price)} mono />}
            {data.can_view_pricing && (
              <div data-testid="part-detail-internal-value">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Internal value</div>
                <div className="text-sm text-slate-800 mt-0.5 font-mono flex items-center gap-2 flex-wrap">
                  {p.internal_value != null ? `${money(p.internal_value)}${p.value_basis ? ` (${p.value_basis})` : ""}` : <span className="text-slate-300">—</span>}
                  {p.internal_value != null && (() => {
                    const vs = p.value_source, st = p.source_tool;
                    let label = "Staff estimate", cls = "bg-blue-50 text-blue-700 border-blue-200", Icon = null;
                    if (vs === "researched") { label = "Researched \u2713"; cls = "bg-emerald-50 text-emerald-800 border-emerald-200"; Icon = CheckCircle2; }
                    else if (vs === "verified_transaction") { label = "Verified at sale"; cls = "bg-teal-50 text-teal-800 border-teal-200"; }
                    else if (st === "ai") { label = "Staff estimate \u00b7 AI assist"; cls = "bg-violet-50 text-violet-700 border-violet-200"; Icon = Sparkles; }
                    else if (st === "perplexity") { label = "Staff estimate \u00b7 Perplexity market analysis"; cls = "bg-sky-50 text-sky-700 border-sky-200"; Icon = Globe; }
                    return <Badge data-testid="part-detail-value-stamp" className={`rounded-full ${cls} border text-[9px] uppercase`}>{Icon && <Icon className="w-2.5 h-2.5 mr-0.5" />}{label}</Badge>;
                  })()}
                </div>
              </div>
            )}
          </div>
        </SectionCard>
      )}

      {/* OUTTAKE — BUILD DEEP-LINK */}
      {data.outtake_builder && (
        <SectionCard testid="part-detail-outtake" title="Pulled into build" icon={Wrench}
          action={<Link to={`/donors/${data.outtake_builder.id}`} state={originState} data-testid="part-detail-outtake-link" className="inline-flex items-center gap-1 text-sm text-[#E0781A] hover:underline">Builder truck <ChevronRight className="w-3.5 h-3.5" /></Link>}>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-slate-500">PO</span>
            <Link to={`/donors/${data.outtake_builder.id}`} state={originState} data-testid="part-detail-outtake-po"
              className="font-mono font-bold text-[#B01E28] hover:underline">{data.outtake_builder.po_number || "—"}</Link>
            <span className="text-slate-300">·</span>
            <Link to={`/donors/${data.outtake_builder.id}`} state={originState} className="font-mono text-slate-700 hover:underline">{data.outtake_builder.stock_number}</Link>
            {data.outtake_builder.vehicle && <span className="text-slate-500">{data.outtake_builder.vehicle}</span>}
          </div>
        </SectionCard>
      )}

      {/* SOURCE TRUCK (traceability) — collapsible, hidden by default */}
      {donor && (
        <SectionCard testid="part-detail-donor" title="Source truck (traceability)" icon={Truck}
          action={<button data-testid="part-donor-toggle" onClick={() => setDonorOpen((v) => !v)} className="inline-flex items-center gap-1 text-sm text-[#E0781A] hover:underline">{donorOpen ? "Hide" : "Show"}<ChevronDown className={`w-3.5 h-3.5 transition-transform ${donorOpen ? "rotate-180" : ""}`} /></button>}>
          {!donorOpen ? (
            <p data-testid="part-detail-donor-collapsed" className="text-sm text-slate-500">
              <span className="font-mono text-slate-700">{donor.stock_number}</span>{[donor.year, donor.make, donor.model].filter(Boolean).length ? ` · ${[donor.year, donor.make, donor.model].filter(Boolean).join(" ")}` : ""} — <button onClick={() => setDonorOpen(true)} className="text-[#E0781A] hover:underline">show traceability</button>
            </p>
          ) : (
            <div className="space-y-3">
              {/* HOW SOMEBODY KNEW, ON THE PART — "show the reason on the part, not just in a log,
                  so it's visible when it matters", and a documented match reads differently from
                  a memory. Only appears when the truck was recorded by a correction, not check-in. */}
              <SourceTruckLine part={p} onDone={load} testid="part-source-line" />
              <div data-testid="part-detail-donor-locked-badge" className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1">
                <Lock className="w-3 h-3" />Donor tie: LOCKED · can never change
              </div>
              <div className="grid grid-cols-1 gap-4">
                <LockedField label="Stock #" value={donor.stock_number} mono testid="part-detail-donor-stock" />
                <LockedField label="VIN" value={donor.vin} mono testid="part-detail-donor-vin" />
                <LockedField label="Vehicle" value={[donor.year, donor.make, donor.model].filter(Boolean).join(" ")} testid="part-detail-donor-vehicle" />
                <LockedField label="Engine" value={donor.engine_model} testid="part-detail-donor-engine" />
              </div>
              <Link to={`/donors/${donor.id}`} state={originState} data-testid="part-detail-donor-link" className="inline-flex items-center gap-1 text-sm text-[#E0781A] hover:underline">Full donor page <ChevronRight className="w-3.5 h-3.5" /></Link>
            </div>
          )}
        </SectionCard>
      )}

      {/* NO SOURCE TRUCK AT ALL — a legacy part nobody has traced yet. "A legacy part with no
          truck is a part I can't tell you anything about." */}
      {!donor && has("assign_source_truck") && (
        <SectionCard testid="part-detail-no-source" title="Source truck" icon={Truck}>
          <p className="text-sm text-slate-500 mb-3">
            Nothing ties this part to a truck yet{p.legacy ? " — it came in as a legacy part" : ""}.
            Recording the truck it came off can be done ONCE, from nothing; it can never be swapped
            from one truck to another afterwards.
          </p>
          <SourceTruckAction part={p} onDone={load} testid="part-detail-source" />
        </SectionCard>
      )}

      {/* MANAGE PHOTOS — archive/restore (viewing lives in the hero viewer above) */}
      {canArchivePhotos && (
        <SectionCard testid="part-detail-photos" title="Manage photos" icon={Camera}
          action={<button data-testid="part-photos-toggle" onClick={() => setPhotosOpen((v) => !v)} className="inline-flex items-center gap-1 text-sm text-[#E0781A] hover:underline">{photosOpen ? "Hide" : "Manage"}<ChevronDown className={`w-3.5 h-3.5 transition-transform ${photosOpen ? "rotate-180" : ""}`} /></button>}>
          {photosOpen
            ? <PhotoGallery photos={p.photos} testid="part-photos" canManage fixScope="part" fixRecordId={id} onFixed={load}
                mainScope="part" mainRecordId={id} mainPhotoFileId={p.main_photo_file_id}
                resolvedMainFileId={p.main_photo?.file_id} onMainChanged={load} />
            : <p data-testid="part-photos-collapsed" className="text-sm text-slate-500">{(p.photos || []).filter((x) => !x.is_archived).length} active photo(s) — <button onClick={() => setPhotosOpen(true)} className="text-[#E0781A] hover:underline">manage / archive</button></p>}
        </SectionCard>
      )}

      {/* HISTORY — bottom, collapsed by default */}
      <SectionCard testid="part-detail-history" title="History" icon={HistoryIcon}
        action={(data.history || []).length > 0 ? (
          <button data-testid="part-history-toggle" onClick={() => setHistOpen((v) => !v)}
            className="inline-flex items-center gap-1 text-sm text-[#E0781A] hover:underline">
            {histOpen ? "Hide" : `View history (${data.history.length})`}
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${histOpen ? "rotate-180" : ""}`} />
          </button>
        ) : null}>
        {(data.history || []).length === 0 ? <p className="text-sm text-slate-400">No history yet.</p> : !histOpen ? (
          <p data-testid="part-history-collapsed" className="text-sm text-slate-500">{data.history.length} events · latest {(data.history[0]?.timestamp || "").slice(0, 10)} — <button onClick={() => setHistOpen(true)} className="text-[#E0781A] hover:underline">view more</button></p>
        ) : (
          <ol className="relative border-l border-slate-200 ml-1.5">
            {data.history.map((h) => (
              <li key={h.id} data-testid={`part-history-${h.tx_number}`} className="ml-4 pb-4 last:pb-0">
                <span className="absolute -left-[5px] mt-1.5 w-2.5 h-2.5 rounded-full bg-[#E0781A] border-2 border-white" />
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-slate-800">{txLabel(h.type)}</span>
                  {h.to_status && <span className="font-mono text-[10px] text-slate-400">→ {statusLabel(h.to_status)}</span>}
                  <span className="ml-auto font-mono text-[10px] text-slate-400">{(h.timestamp || "").slice(0, 16).replace("T", " ")}</span>
                </div>
                {h.type === "part.protected_edit" && h.extra?.changes && (
                  <div data-testid={`part-protected-change-${h.tx_number}`} className="mt-1 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px]">
                    {h.extra.changes.map((c, i) => (
                      <div key={i} className="text-amber-900">
                        <span className="font-semibold">{c.field}</span>: <span className="font-mono line-through text-amber-700">{c.from}</span> → <span className="font-mono font-semibold">{c.to}</span>
                      </div>
                    ))}
                    {h.reason && <div className="text-amber-700 mt-0.5">Reason: {h.reason}</div>}
                  </div>
                )}
                <div className="font-mono text-[10px] text-slate-400 mt-0.5">{h.tx_number} · {h.user_email}{h.reason && h.type !== "part.protected_edit" ? ` · ${h.reason}` : ""}</div>
              </li>
            ))}
          </ol>
        )}
      </SectionCard>

        </div>
      </div>

      {/* Tag dialog */}

      <PartTagPrintDialog part={data?.part ? { id, short_code: data.part.short_code } : null} open={labelOpen} onClose={() => setLabelOpen(false)} canReprint={canReprint} />

      <SpinOffChildDialog open={spinOpen} onOpenChange={setSpinOpen} part={data?.part} initialNumberId={spinNumberId} onDone={() => load()} />

      {/* "You have 3 unsaved child parts — keep editing or discard?" — his words, 2026-10-15. */}
      <UnsavedWorkDialog open={askDiscard} onOpenChange={setAskDiscard} count={splitWorkCount}
        noun="child part" onDiscard={discardSplit} testid="split-unsaved" />


      {/* Edit lives in the full Identify & Completion workspace (/sort-queue/:id) */}

      {/* SPLIT dialog */}
      <Dialog open={splitOpen} onOpenChange={(v) => !v && closeSplit()}>
        <DialogContent className="rounded-xl border-slate-200 sm:max-w-lg" data-testid="part-detail-split-dialog">
          <DialogHeader><DialogTitle className="font-display">Split {p.short_code}</DialogTitle>
            <DialogDescription>Scan or type a tag for each child — that's all you need. Name them later in Sort &amp; Identify. The parent stays available.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {draftBack && splitWorkCount > 0 && (
              <div data-testid="split-draft-restored" className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900">
                <b>Picked up where you left off.</b> {splitWorkCount} child part{splitWorkCount === 1 ? "" : "s"} you
                started {splitWorkCount === 1 ? "is" : "are"} still here — nothing has been created yet.
              </div>
            )}
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-2">
              <ScanField testid="split-child-scan" label="Scan each child's tag" placeholder="Scan gun · camera · or type a tag…" submitLabel="Add child" onScan={addScannedChild} continuous uppercase autoFocus={false} />
              <p className="text-[11px] text-slate-500 mt-1">Scan on a tablet/phone camera or a scan gun — each scan adds a child below. You can still type tags manually.</p>
            </div>
            {splitChildren.map((c, i) => (
              <div key={i} className="rounded-lg border border-slate-200 p-2 space-y-2">
                {/* On a phone the tag box + its camera button and the optional name do not fit on
                    one line: his iPhone showed "FAS-9A…" in a box 6 characters wide. The name
                    drops below the tag from `sm` down. */}
                <div className="flex flex-col sm:flex-row gap-2">
                  <ScanTextInput testid={`split-child-tag-${i}`} placeholder={`Scan/type tag for child ${i + 1}`}
                    value={c.tag_code || ""} onChange={(v) => patchChild(i, { tag_code: v })} resolveTag
                    onResolved={(r) => { if (r.error) toast.error(r.error); else if (r.boundPart) toast.error(tagStatusWords(r)); }}
                    className="flex-1 min-w-0" />
                  <Input data-testid={`split-child-${i}`} placeholder="Name (optional)" value={c.name} onChange={(e) => patchChild(i, { name: e.target.value })} className="rounded-lg border-slate-200 w-40" />
                </div>
                {((p.numbers || []).length > 0 || (p.keywords || []).length > 0) && (
                  <div>
                    <button type="button" data-testid={`split-child-refs-toggle-${i}`} onClick={() => patchChild(i, { showRefs: !c.showRefs })}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#B01E28] hover:underline">
                      <GitBranch className="w-3.5 h-3.5" />{c.showRefs ? "Hide" : "Move numbers/keywords to this child"}
                      {(c.sel.size || c.selKw.size) ? <span className="font-mono text-[10px] rounded px-1 bg-orange-100 text-[#B45309]">{c.sel.size + c.selKw.size}</span> : null}
                    </button>
                    {c.showRefs && (
                      <div className="mt-2 rounded-lg bg-slate-50/70 p-2">
                        <ReferencePicker
                          numbers={p.numbers || []} keywords={p.keywords || []} primaryNorm={p.oem_normalized} primaryValue={p.oem_number} supersededNorms={p.superseded_normalized || []} prefix={`split-c${i}`}
                          sel={c.sel} onToggleNum={(idn) => toggleInChildSet(i, "sel", idn)}
                          keepNum={c.keepNum} onToggleKeepNum={(idn) => toggleInChildSet(i, "keepNum", idn)}
                          selKw={c.selKw} onToggleKw={(k) => toggleInChildSet(i, "selKw", k)}
                          keepKw={c.keepKw} onToggleKeepKw={(k) => toggleInChildSet(i, "keepKw", k)}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            <Button type="button" size="sm" variant="outline" className="rounded-lg border-slate-200" onClick={() => setSplitChildren((s) => [...s, newSplitChild()])}><Plus className="w-3.5 h-3.5 mr-1" />Add child</Button>
            {splitFlagged.length > 0 && (
              <div data-testid="split-freetext-flag" className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>The assembly's name/notes mention <b>{splitFlagged.join(", ")}</b>. I won't auto-edit your text — after this, edit the assembly's name/notes so a search doesn't still surface it.</span>
              </div>
            )}
            {splitChildren.some((c) => c.sel.size || c.selKw.size) && (
              <div className="pt-1">
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Note on the parent (required — references are moving)</label>
                <Textarea data-testid="split-note" value={splitNote} onChange={(e) => setSplitNote(e.target.value)} rows={2} placeholder="e.g. Doser + sensor pulled off and split out to sell separately." className="rounded-lg mt-1 text-sm" />
                <p className="text-[11px] text-slate-400 mt-1">Moved numbers/keywords leave the parent and land on each child; a link back to each child is recorded automatically.</p>
              </div>
            )}
          </div>
          {/* PINNED, SO THE BUTTON IS ALWAYS UNDER A THUMB however many children are on the list
              (his phone, 2026-10-15). The count is on the button so nobody has to scroll back up
              to see how many they are about to create. */}
          <DialogFooter>
            <Button variant="outline" className="rounded-lg border-slate-200" onClick={() => closeSplit()}>Cancel</Button>
            <Button data-testid="part-detail-confirm-split" disabled={splitting} onClick={doSplit} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white">
              {splitting ? <Loader2 className="w-4 h-4 animate-spin" />
                : `Create ${splitWorkCount || ""} child part${splitWorkCount === 1 ? "" : "s"}`.replace("  ", " ")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* LINK / REASSIGN PARENT */}
      <Dialog open={linkOpen} onOpenChange={(v) => !v && setLinkOpen(false)}>
        <DialogContent className="rounded-xl border-slate-200 sm:max-w-md" data-testid="part-detail-link-dialog">
          <DialogHeader><DialogTitle className="font-display">{data.family?.parent ? "Change parent" : "Link to a parent assembly"}</DialogTitle>
            <DialogDescription>Scan or type the parent part's tag. Both parts must be from the <span className="font-semibold">same donor truck</span>. This changes only the parent link — the donor tie never changes.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <ScanTextInput testid="link-parent-input" autoFocus placeholder="Scan/type parent tag (FAS-…)"
                value={linkParentCode}
                onChange={(v) => { setLinkParentCode(v); setLinkPreview(null); }}
                onEnter={resolveParent} className="flex-1" />
              <Button data-testid="link-parent-resolve" variant="outline" disabled={linkResolving || !linkParentCode.trim()} onClick={resolveParent} className="rounded-lg border-slate-300">{linkResolving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Find"}</Button>
            </div>
            {linkPreview && (
              <div data-testid="link-parent-preview" className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 flex items-center gap-2">
                <Package className="w-4 h-4 text-[#B01E28]" />
                <span className="font-mono text-xs font-bold text-[#B01E28]">{linkPreview.tag_code || linkPreview.short_code}</span>
                <span className="flex-1 text-sm text-slate-800 truncate">{[linkPreview.year, linkPreview.make, linkPreview.model].filter(Boolean).join(" ") || linkPreview.part_type || "—"}</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg border-slate-200" onClick={() => setLinkOpen(false)}>Cancel</Button>
            <Button data-testid="part-detail-confirm-link" disabled={linking || !linkPreview} onClick={doLink} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white">{linking ? <Loader2 className="w-4 h-4 animate-spin" /> : (data.family?.parent ? "Reassign parent" : "Link")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
